import {Args} from '@oclif/core'
import {SanityCommand, subdebug} from '@sanity/cli-core'
import {select} from '@sanity/cli-core/ux'

import {type Hook} from '../../actions/hook/types'
import {promptForProject} from '../../prompts/promptForProject.js'
import {deleteHookForProject, listHooksForProject} from '../../services/hooks.js'
import {getProjectIdFlag} from '../../util/sharedFlags.js'

const deleteHookDebug = subdebug('hook:delete')

export class Delete extends SanityCommand<typeof Delete> {
  static override args = {
    name: Args.string({
      description: 'Name of webhook to delete (will prompt if not provided)',
      required: false,
    }),
  }

  static override description = 'Delete a webhook from the project'

  static override examples = [
    {
      command: '<%= config.bin %> <%= command.id %>',
      description: 'Interactively select and delete a webhook',
    },
    {
      command: '<%= config.bin %> <%= command.id %> my-hook',
      description: 'Delete a specific webhook by name',
    },
    {
      command: '<%= config.bin %> <%= command.id %> --project-id abc123',
      description: 'Delete a webhook from a specific project',
    },
  ]

  static override flags = {
    ...getProjectIdFlag({
      description: 'Project ID to delete webhook from',
      semantics: 'override',
    }),
  }

  static override hiddenAliases: string[] = ['hook:delete']

  public async run(): Promise<void> {
    const {args} = await this.parse(Delete)

    const projectId = await this.getProjectId({
      fallback: () =>
        promptForProject({
          requiredPermissions: [{grant: 'delete', permission: 'sanity.project.webhooks'}],
        }),
    })

    // Get the hook ID to delete
    const hookId = await this.promptForHook(args.name, projectId)

    try {
      await deleteHookForProject(projectId, hookId)

      this.log('Hook deleted')
    } catch (error) {
      const err = error as Error
      deleteHookDebug(`Error deleting hook ${hookId} for project ${projectId}`, err)
      this.error(`Hook deletion failed:\n${err.message}`, {exit: 1})
    }
  }

  private async promptForHook(
    specifiedName: string | undefined,
    projectId: string,
  ): Promise<string> {
    let hooks: Hook[]
    try {
      hooks = await listHooksForProject(projectId)
    } catch (error) {
      const err = error as Error
      deleteHookDebug(`Error fetching hooks for project ${projectId}`, err)
      this.error(`Failed to fetch hooks:\n${err.message}`, {exit: 1})
    }

    if (hooks.length === 0) {
      this.error('No hooks configured for this project.', {exit: 1})
    }

    // If hook name is specified, find it in the list
    if (specifiedName) {
      const specifiedNameLower = specifiedName.toLowerCase()
      const selectedHook = hooks.find((hook) => hook.name.toLowerCase() === specifiedNameLower)

      if (!selectedHook) {
        this.error(`Hook with name "${specifiedName}" not found`, {exit: 1})
      }

      return selectedHook.id
    }

    // If no hook name specified, prompt user to select one
    const choices = hooks.map((hook) => ({
      name: hook.name,
      value: hook.id,
    }))

    const selectedId = await select({
      choices,
      message: 'Select hook to delete',
    })

    return selectedId
  }
}
